import type { Href } from "expo-router";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import * as Clipboard from "expo-clipboard";
import { useCallback, useMemo, useRef, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import {
  addUserToCompany,
  findUserByEmail,
  listAllCompanies,
  listAllCompetitions,
  listCompanyMembers,
  type AdminCompanyListRow,
  type AdminCompetitionListRow,
  type AdminCompanyMemberRow,
} from "../../../../lib/admin";
import { adminCreateRmCase } from "../../../../lib/caseApprovals";
import { logAdminAction } from "../../../../lib/auditLog";
import { useAuth } from "../../../../lib/authContext";
import { buildInviteUrl, createInvite } from "../../../../lib/invites";
import { RADII, SPACING, STROKES } from "../../../../lib/brand";
import { PANE_THEMES } from "../../../../components/brand/paneThemes";
import {
  AdminBanner,
  AdminButton,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminModal,
  AdminPageHeader,
  FilterChip,
  ListRow,
  ListRowGroup,
} from "../../../../components/admin/ui";
import { SectionHeader } from "../../../../components/aspects/SectionHeader";
import { StatusPill, type StatusPillTone } from "../../../../components/aspects/StatusPill";

const THEME = PANE_THEMES.Admin;

function compStatusTone(status: string): StatusPillTone {
  if (status === "open") return "sage";
  if (status === "judging") return "amber";
  return "grey";
}

export default function AdminCompanyDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { role } = useAuth();
  const [company, setCompany] = useState<AdminCompanyListRow | null>(null);
  const [members, setMembers] = useState<AdminCompanyMemberRow[]>([]);
  const [competitions, setCompetitions] = useState<AdminCompetitionListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [companyRole, setCompanyRole] = useState<"owner" | "member">("member");
  const [err, setErr] = useState<string | null>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  const [inviteExpires, setInviteExpires] = useState("");
  const [creatingCase, setCreatingCase] = useState(false);
  const [confirmNoEngagement, setConfirmNoEngagement] = useState(false);
  const hasLoadedRef = useRef(false);
  const lastLoadIdRef = useRef<string | undefined>(undefined);

  const canCreateRmCase = role === "super_admin" || role === "admin_rm";

  const load = useCallback(async () => {
    if (typeof id === "string" && lastLoadIdRef.current !== id) {
      hasLoadedRef.current = false;
      lastLoadIdRef.current = id;
    }
    if (!hasLoadedRef.current) setLoading(true);
    try {
      if (typeof id !== "string") return;
      setErr(null);
      const [companies, memberRows, competitionRows] = await Promise.all([
        listAllCompanies(),
        listCompanyMembers(id),
        listAllCompetitions(),
      ]);
      setCompany(companies.find((c) => c.id === id) ?? null);
      setMembers(memberRows);
      setCompetitions(competitionRows.filter((c) => c.company_id === id));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, [id]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  async function onAddUser() {
    if (typeof id !== "string" || !email.trim()) return;
    setErr(null);
    const user = await findUserByEmail(email);
    if (!user) {
      setErr("No user found with that email.");
      return;
    }
    try {
      await addUserToCompany(user.id, id, companyRole);
      setEmail("");
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to add user");
    }
  }

  async function onCreateRmCase() {
    if (typeof id !== "string" || !company || creatingCase) return;
    setErr(null);
    const target = members.find((m) => m.role === "owner") ?? members[0] ?? null;
    if (!target) {
      setErr("This company has no users yet. Invite a client user before creating a case for them.");
      return;
    }
    setCreatingCase(true);
    try {
      const caseId = await adminCreateRmCase(target.user_id, id);
      void logAdminAction({
        action: "competition.admin_created_rm",
        entityType: "competitions",
        entityId: caseId,
        summary: `Created Coravu-Managed case for ${target.full_name ?? target.email ?? "client"} (${company.name})`,
        diff: { after: { client_user_id: target.user_id, company_id: id, engagement_model: "rm" } },
      });
      router.push(`/admin/cases/${caseId}/edit` as Href);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to create case");
    } finally {
      setCreatingCase(false);
      setConfirmNoEngagement(false);
    }
  }

  function onClickCreateRmCase() {
    if (!company) return;
    if (company.engagement_model === "rm") {
      void onCreateRmCase();
      return;
    }
    if (company.engagement_model == null) {
      setConfirmNoEngagement(true);
      return;
    }
    setErr("Only Coravu-Managed (RM) clients can have admin-created cases.");
  }

  async function onCreateClientInvite() {
    if (typeof id !== "string") return;
    if (!inviteEmail.trim()) {
      setErr("Invite email is required.");
      return;
    }
    setInviteBusy(true);
    setErr(null);
    try {
      const row = await createInvite({ kind: "client", email: inviteEmail.trim(), companyId: id, role: "client" });
      setInviteUrl(buildInviteUrl(row.token));
      setInviteExpires(new Date(row.expires_at).toLocaleString());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to create invite");
    } finally {
      setInviteBusy(false);
    }
  }

  const title = useMemo(() => company?.name ?? "Company", [company]);

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: "center", backgroundColor: THEME.pageBg }}>
        <AdminLoading theme={THEME} label="Loading company…" />
      </View>
    );
  }

  const showCreateCase = canCreateRmCase && (company?.engagement_model === "rm" || company?.engagement_model == null);

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 40 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 920 }}>
        <Pressable
          onPress={() => {
            if (router.canGoBack()) router.back();
            else router.replace("/admin/clients" as Href);
          }}
          style={[
            { alignSelf: "flex-start", paddingVertical: 4, marginBottom: 6 } as ViewStyle,
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11.5, letterSpacing: 1.2, textTransform: "uppercase", color: THEME.textOnSurfaceDim }}>
            ← Back
          </Text>
        </Pressable>

        <AdminPageHeader
          theme={THEME}
          eyebrow="People · Company"
          title={title}
          subtitle={`${company?.size ?? "—"} · ${company?.industry ?? "—"} · ${company?.engagement_model ?? "—"}`}
          actions={
            <>
              <AdminButton theme={THEME} label="Invite client user" variant="outline" size="sm" onPress={() => { setInviteModalOpen(true); setInviteEmail(""); setInviteUrl(""); setInviteExpires(""); setErr(null); }} />
              <AdminButton theme={THEME} label="+ Create RM program" size="sm" onPress={() => router.push(`/admin/programs/new-rm?companyId=${id}` as Href)} />
              {showCreateCase ? (
                <AdminButton theme={THEME} label="+ Create case" variant="outline" size="sm" loading={creatingCase} onPress={onClickCreateRmCase} />
              ) : null}
            </>
          }
        />

        {err ? <View style={{ marginBottom: SPACING.md }}><AdminError theme={THEME} message={err} onRetry={() => void load()} /></View> : null}

        {/* Users */}
        <SectionHeader theme={THEME} title="Users" tone="dark" count={members.length} />
        {members.length === 0 ? (
          <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim, marginBottom: 12 }}>No users yet.</Text>
        ) : (
          <ListRowGroup theme={THEME} style={{ marginBottom: 12 }}>
            {members.map((m, i) => (
              <ListRow key={m.user_id} theme={THEME} first={i === 0} title={m.full_name ?? "User"} subtitle={m.email ?? "—"} meta={m.role} />
            ))}
          </ListRowGroup>
        )}

        {/* Add user */}
        <View style={{ padding: 16, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: THEME.border }}>
          <SectionHeader theme={THEME} title="Add user to company" tone="dark" />
          <AdminInput theme={THEME} value={email} onChangeText={setEmail} placeholder="user email" autoCapitalize="none" keyboardType="email-address" />
          <View style={{ marginTop: 10, flexDirection: "row", gap: 6 }}>
            <FilterChip theme={THEME} label="member" active={companyRole === "member"} onPress={() => setCompanyRole("member")} />
            <FilterChip theme={THEME} label="owner" active={companyRole === "owner"} onPress={() => setCompanyRole("owner")} />
          </View>
          <View style={{ marginTop: 12 }}>
            <AdminButton theme={THEME} label="Add user" onPress={() => void onAddUser()} />
          </View>
        </View>

        {/* Competitions */}
        <View style={{ marginTop: SPACING.lg }}>
          <SectionHeader theme={THEME} title="Competitions" tone="dark" count={competitions.length} />
          {competitions.length === 0 ? (
            <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim }}>No competitions yet.</Text>
          ) : (
            <ListRowGroup theme={THEME}>
              {competitions.map((c, i) => (
                <ListRow
                  key={c.id}
                  theme={THEME}
                  first={i === 0}
                  onPress={() => router.push(`/client/competition/${c.id}` as Href)}
                  title={c.name}
                  trailing={<StatusPill theme={THEME} label={c.status} tone={compStatusTone(c.status)} />}
                />
              ))}
            </ListRowGroup>
          )}
        </View>
      </View>

      {/* No-engagement confirm */}
      {confirmNoEngagement ? (
        <AdminModal
          theme={THEME}
          visible
          onClose={() => setConfirmNoEngagement(false)}
          title="No engagement model set"
          subtitle="This client doesn't have an engagement model set yet. Creating a case will mark them as Coravu-Managed (RM). Continue?"
          footer={
            <>
              <AdminButton theme={THEME} label="Cancel" variant="ghost" disabled={creatingCase} onPress={() => setConfirmNoEngagement(false)} />
              <AdminButton theme={THEME} label="Continue and create case" loading={creatingCase} onPress={() => void onCreateRmCase()} />
            </>
          }
        />
      ) : null}

      {/* Invite modal */}
      {inviteModalOpen ? (
        <AdminModal
          theme={THEME}
          visible
          onClose={() => { setInviteModalOpen(false); setInviteUrl(""); }}
          title="Invite client user"
          subtitle="Send the link manually until email is configured."
          footer={<AdminButton theme={THEME} label="Close" variant="ghost" onPress={() => { setInviteModalOpen(false); setInviteUrl(""); }} />}
        >
          {!inviteUrl ? (
            <View style={{ gap: 12 }}>
              <AdminInput theme={THEME} label="Email" value={inviteEmail} onChangeText={setInviteEmail} autoCapitalize="none" keyboardType="email-address" placeholder="colleague@company.com" />
              {err ? <AdminBanner theme={THEME} tone="error" message={err} /> : null}
              <AdminButton theme={THEME} label="Create invite" loading={inviteBusy} onPress={() => void onCreateClientInvite()} />
            </View>
          ) : (
            <View style={{ gap: 10 }}>
              <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12.5, color: THEME.textOnSurfaceDim }}>Invite link (expires {inviteExpires})</Text>
              <Text selectable style={{ padding: 10, borderRadius: RADII.sm, borderWidth: STROKES.hairline, borderColor: THEME.border, backgroundColor: THEME.altBg, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurface }}>
                {inviteUrl}
              </Text>
              <AdminButton theme={THEME} label="Copy link" onPress={() => void Clipboard.setStringAsync(inviteUrl)} />
            </View>
          )}
        </AdminModal>
      ) : null}
    </ScrollView>
  );
}
